import * as types from './mutation-types'
import { services } from './api'
import util from 'assets/js/util.js'
import Vue from 'vue'
import store from 'vuex/store.js'

/**************************************************************************
Authentication
**************************************************************************/

/**
  * @desc   Calls auth API method and evaluates response
  * @param  parameters object - Log in Data
  * @return object - Response
*/
export const logIn = (context, parameters) => {
  return services.auth.retrieveToken(parameters)
  .then((response) => {
    if (response.data.success) {
      let data = response.data.data
      context.commit(types.AUTH_LOGGED_IN, data)
      setPermissions(context, data.accounts)
      setUserInfo(context, data, parameters)
      setUserAccounts(context, data.accounts)
    } else {
      response.data.errors.forEach(function (error) {
        console.log(error)
      })
    }
  })
  .catch((error) => {
    console.error(error)
  })
}

/**
  * @desc   Logs out and resets default values
*/
export const logOut = (context) => {
  context.commit(types.AUTH_LOGGED_OUT)
  context.commit(types.DASHBOARD_RESET_FILTER)
  context.commit(types.INVENTORY_RESET_VALUES)
  context.commit(types.PRODUCT_RESET_VALUES)
  context.commit(types.MONITORING_DOCUMENT_TYPES_RESET)
  context.commit(types.MONITORING_DOCUMENT_SEARCH_RESET)
  context.commit(types.ORDERPROGRESS_SET_ORDER_RESET)
}

/**
  * @desc   Sets logged in account info
  * @param  username string - Log in User
*/
function setUserInfo (context, data, parameters) {
  let userinfo = {
    userID: data.username,
    name: data.name,
    position: '',
    createdAt: '',
    encodedToken: btoa(`${parameters.Username}:${parameters.Password}`)
  }
  context.commit(types.USER_FETCH_INFO, userinfo = JSON.stringify(userinfo))
}

/**
  * @desc   Sets logged in account associated accounts (clients)
*/
function setUserAccounts (context, accounts) {
  accounts = accounts.map(account => ({
    code: account.accountCode,
    name: Vue.options.filters.formatFirstLetter(account.description),
    hasWarehouseService: account.hasWarehouseService,
    hasTransportService: account.hasTransportService,
    hasTemporaryDepositService: account.hasTemporaryDepositService
  }))
  // accounts.unshift({code: 'AVI15', name: 'Test'})
  context.commit(types.USER_FETCH_CLIENTS, JSON.stringify(accounts))
  context.commit(types.DASHBOARD_SET_FILTER_CLIENT, accounts[0])
}

/**
  * @desc   Sets logged in account associated menu
  * @param  data object - Menu options permissions
*/
function setPermissions (context, data) {
  let permissions = {}
  let defaultMenu = {}
  data.forEach(account => {
    let accountItem = { code: account.accountCode, name: account.description }
    account.permissions.forEach(item => {
      if (!permissions[item.permission]) {
        permissions[item.permission] = []
      }
      permissions[item.permission].push(accountItem)
    })
  })

  defaultMenu = data[0].permissions[0]

  context.commit(types.USER_SET_PERMISSIONS, JSON.stringify(permissions))
  context.commit(types.USER_SET_DEFAULT_MENU_ITEM, JSON.stringify(defaultMenu))
  context.commit(types.USER_SET_SLIDE_MENU_ITEMS, util.formatSlideMenuItems(permissions))
}

/**************************************************************************
Dashboard
**************************************************************************/

/**
  * @desc   Sets dashboard filter selected date
  * @param  parameters object - Dashboard Filter
*/
export const setFilterDate = (context, parameters) => {
  context.commit(types.DASHBOARD_SET_FILTER_DATE, parameters)
}

/**
  * @desc   Sets dashboard filter selected client
  * @param  client object - Dashboard Filter Client
*/
export const setFilterClient = (context, client) => {
  context.commit(types.DASHBOARD_SET_FILTER_CLIENT, client)
}

/**
  * @desc   Calls Dashboard API method and fetches data
  * @param  parameters object - Dashboard Filter
  * @return object - Dashboard Data
*/
export const fetchDashboardData = (context, parameters) => {
  return services.dashboard.retrieveDashboardData(parameters)
  .then((response) => {
    if (response.data.success) {
      return response.data.data
    } else {
      response.data.errors.forEach(function (error) {
        console.log(error)
      })
    }
  })
  .catch((error) => {
    console.error(error)
  })
}

/**************************************************************************
Reception
**************************************************************************/

/**
  * @desc   Calls Reception Appointment Document List API method and fetches data
  * @param  accountCode string - Account Code
  * @return object - Reception Appointment Document List
*/
export const fetchReceptionAppointmentDocumentList = (context, accountCode) => {
  return services.reception.retrieveAppointmentDocumentList(accountCode)
  .then((response) => {
    if (response.data.success) {
      return response.data.data
    } else {
      response.data.errors.forEach(function (error) {
        console.log(error)
      })
    }
  })
  .catch((error) => {
    console.error(error)
  })
}

/**
  * @desc   Calls Reception Appointment Upload file API method and evaluates response
  * @param  parameters Object - AccountCode string and File Binary
  * @return object - Response
*/
export const uploadReceptionApointmentFile = (context, parameters) => {
  return services.reception.uploadApointmentFile(parameters)
  .then((response) => {
    if (!response.data.success) {
      response.data.errors.forEach(function (error) {
        console.log(error)
      })
    }
    return response.data
  })
  .catch(() => {
    return {data: {success: false}}
  })
}

/**
  * @desc   Calls Reception Appointment Save file API method and evaluates response
  * @param  parameters Object - AccountCode string and Uploaded File Data
  * @return object - Response
*/
export const saveReceptionApointmentFile = (context, parameters) => {
  return services.reception.saveApointmentFile(parameters)
  .then((response) => {
    if (!response.data.success) {
      response.data.errors.forEach(function (error) {
        console.log(error)
      })
    }
    return response.data
  })
  .catch(() => {
    return {data: {success: false}}
  })
}

/**************************************************************************
Dispatch
**************************************************************************/

/**
  * @desc   Calls Dispatch Appointment Document List API method and fetches data
  * @param  accountCode string - Account Code
  * @return object - Dispatch Appointment Document List
*/
export const fetchDispatchAppointmentDocumentList = (context, accountCode) => {
  return services.dispatch.retrieveAppointmentDocumentList(accountCode)
  .then((response) => {
    if (response.data.success) {
      return response.data.data
    } else {
      response.data.errors.forEach(function (error) {
        console.log(error)
      })
    }
  })
  .catch((error) => {
    console.error(error)
  })
}

/**
  * @desc   Calls Dispatch Appointment Upload file API method and evaluates response
  * @param  parameters Object - AccountCode string and File Binary
  * @return object - Response
*/
export const uploadDispatchApointmentFile = (context, parameters) => {
  return services.dispatch.uploadApointmentFile(parameters)
  .then((response) => {
    if (!response.data.success) {
      response.data.errors.forEach(function (error) {
        console.log(error)
      })
    }
    return response.data
  })
  .catch(() => {
    return {data: {success: false}}
  })
}

/**
  * @desc   Calls Dispatch Appointment Save file API method and evaluates response
  * @param  parameters Object - AccountCode string and Uploaded File Data
  * @return object - Response
*/
export const saveDispatchApointmentFile = (context, parameters) => {
  return services.dispatch.saveApointmentFile(parameters)
  .then((response) => {
    if (!response.data.success) {
      response.data.errors.forEach(function (error) {
        console.log(error)
      })
    }
    return response.data
  })
  .catch(() => {
    return {data: {success: false}}
  })
}

/**
  * @desc   Calls Vehicle List API method and fetches data
  * @param  accountCode string - Account Code
  * @return object - Vehicle List Data
*/
export const fetchDispatchVehicleList = (context, accountCode) => {
  return services.dispatch.retrieveVehicleList(accountCode)
  .then((response) => {
    if (response.data.success) {
      return response.data.data
    } else {
      response.data.errors.forEach(function (error) {
        console.log(error)
      })
    }
  })
  .catch((error) => {
    console.error(error)
  })
}

/**
  * @desc   Calls Vehicle Location API method and fetches data
  * @param  vehiclePlate string - Vehicle Plate
  * @return object - Vehicle location Data
*/
export const fetchDispatchVehicleLocation = (context, vehiclePlate) => {
  return services.dispatch.retrieveVehicleLocation(vehiclePlate)
  .then((response) => {
    if (response.data.success) {
      return response.data.data
    } else {
      response.data.errors.forEach(function (error) {
        console.log(error)
      })
    }
  })
  .catch((error) => {
    console.error(error)
  })
}

/**
  * @desc   Calls Document Search API method and fetches data
  * @param  parameters object - Document Search Filter
  * @return object - Document Search Data
*/
export const searchDocument = (context, parameters) => {
  return services.monitoring.retrieveDocumentList(parameters)
  .then((response) => {
    if (response.data.success) {
      context.commit(types.MONITORING_SET_DOCUMENT_SEARCH, {
        filter: parameters,
        result: response.data.data.documents
      })

      return response.data.data.documents
    } else {
      response.data.errors.forEach(function (error) {
        console.log(error)
      })
    }
  })
  .catch((error) => {
    console.error(error)
  })
}

export const searchDocument2 = (context, parameters) => {
  return services.monitoring.retrieveDocumentList(parameters)
  .then((response) => {
    if (response.data.success) {
      context.commit(types.MONITORING_DOCUMENT_SEARCH_RESET)
      context.commit(types.MONITORING_SET_DOCUMENT_SEARCH, {
        filter: parameters,
        result: response.data.data.documents,
        pages: response.data.data.totalPages
      })
      return response.data.data
    } else {
      response.data.errors.forEach(function (error) {
        console.log(error)
      })
    }
  })
  .catch((error) => {
    console.error(error)
  })
}

/**
  * @desc   Saves Search Document Page
*/
export const searchDocumentPage = (context, page) => {
  context.commit(types.MONITORING_SET_DOCUMENT_SEARCH_PAGE, page)
}

export const getDocument = (context, parameters) => {
  return services.monitoring.retrieveDocument(parameters)
  .then((response) => {
    if (response.data.success) {
      return response.data.data
    } else {
      response.data.errors.forEach(function (error) {
        console.log(error)
      })
    }
  })
  .catch((error) => {
    console.error(error)
  })
}

export const getDocumentTypes = (context, parameters) => {
  return services.monitoring.retrieveDocumentTypes(parameters).then((response) => {
    if (response.data.success) {
      context.commit(types.MONITORING_SET_DOCUMENT_TYPES, response.data.data.items)
      return response.data.data.items
    } else {
      response.data.errors.forEach(function (error) {
        console.log(error)
      })
    }
  })
  .catch((error) => {
    console.error(error)
  })
}

export const getDocumentImage = (context, parameters) => {
  return services.monitoring.retrieveDocumentImages(parameters).then((response) => {
    if (response.data.success) {
      context.commit(types.MONITORING_SET_DOCUMENT_IMAGE, response.data.data)
      return response.data.data
    } else {
      response.data.errors.forEach(function (error) {
        console.log(error)
      })
    }
  })
  .catch((error) => {
    console.error(error)
  })
}

/**************************************************************************
Tracking
**************************************************************************/

/**
  * @desc   Calls Tracking API method and fetches data
  * @param  parameters object - Tracking parameter
  * @return object - Tracking Data
  * @author ATI - WCR
*/
export const getTracking = (context, parameters) => {
  return services.tracking.retrieveTracking(parameters).then((response) => {
    if (response.data !== undefined) {
      // context.commit(types.TRACKING_SET_ORDER_TYPES, response.data.orderList)
      return response.data
    } else {
      console.log(response.data.errorDescription)
    }
  })
  .catch((error) => {
    if (error.status === 400) {
      return error.data
    } else {
      console.error(error)
    }
  })
}

/**
  * @desc   Calls Point Tracking API method and fetches data
  * @param  parameters object - Tracking parameter
  * @return object - Tracking Data
  * @author ATI - WCR
*/
export const getPointTracking = (context, parameters) => {
  return services.tracking.retrievePointTracking(parameters).then((response) => {
    if (response.data !== undefined) {
      // context.commit(types.TRACKING_SET_ORDER_TYPES, response.data.orderList)
      return response.data
    } else {
      console.log(response.data.errorDescription)
    }
  })
  .catch((error) => {
    if (error.status === 400) {
      return error.data
    } else {
      console.error(error)
    }
  })
}

/**
  * @desc   Calls Ubigeo API method and fetches data
  * @param  parameters object - Ubigeo parameter
  * @return object - Ubigeo Data
  * @author ATI - WCR
*/
export const getUbigeo = (context, parameters) => {
  return services.tracking.retrieveUbigeoList(parameters).then((response) => {
    if (response.data !== undefined) {
      return response.data
    } else {
      console.log(response.data.errorDescription)
    }
  })
  .catch((error) => {
    console.error(error)
  })
}

/**
  * @desc   Calls Order Type API method and fetches data
  * @return object - Type Data
  * @author ATI - WCR
*/
export const getOrderType = () => {
  return services.tracking.retrieveOrderTypeList().then((response) => {
    if (response.data !== undefined) {
      return response.data
    } else {
      console.log(response.data.errorDescription)
    }
  })
  .catch((error) => {
    console.error(error)
  })
}

/**
  * @desc   Calls Order Status API method and fetches data
  * @return object - Status Data
  * @author ATI - WCR
*/
export const getOrderStatus = () => {
  return services.tracking.retrieveStatusList().then((response) => {
    if (response.data !== undefined) {
      return response.data
    } else {
      console.log(response.data.errorDescription)
    }
  })
  .catch((error) => {
    console.error(error)
  })
}

/**
  * @desc   Calls Order Zone API method and fetches data
  * @return object - Zone Data
  * @author ATI - WCR
*/
export const getOrderZone = () => {
  return services.tracking.retrieveZoneList().then((response) => {
    if (response.data !== undefined) {
      return response.data
    } else {
      console.log(response.data.errorDescription)
    }
  })
  .catch((error) => {
    console.error(error)
  })
}

/**
  * @desc   Calls Order Follow API method and fetches data
  * @param  parameters object - Ubigeo parameter
  * @return object - Order Follow Data
  * @author ATI - WCR
*/
export const getOrderFollow = (context, parameters) => {
  return services.tracking.retrieveOrderFollowUpList(parameters, parameters.Page, parameters.PageSize).then((response) => {
    if (response.data !== undefined) {
      return response.data
    } else {
      console.log(response.data.errorDescription)
    }
  })
  .catch((error) => {
    if (error.status === 400) {
      return error.data
    } else {
      console.error(error)
    }
  })
}

/**
  * @desc   Calls Order Detail API method and fetches data
  * @param  parameters object - Ubigeo parameter
  * @return object - Order Detail Data
  * @author ATI - WCR
*/
export const getOrderFollowDetail = (context, parameters) => {
  return services.tracking.retrieveOrderDetail(parameters).then((response) => {
    if (response.data !== undefined) {
      return response.data
    } else {
      console.log(response.data.errorDescription)
    }
  })
  .catch((error) => {
    console.error(error)
  })
}

/**
  * @desc   Calls Order Report API method and fetches data
  * @param  parameters object - Ubigeo parameter
  * @return object - Order Report Data
  * @author ATI - WCR
*/
export const getOrderReport = (context, parameters) => {
  return services.tracking.retrieveGenerateReport(parameters).then((response) => {
    if (response.data !== undefined) {
      return response.data
    } else {
      console.log(response.data.errorDescription)
    }
  })
  .catch((error) => {
    if (error.status === 400) {
      return error.data
    } else {
      console.error(error)
    }
  })
}

/**************************************************************************
Alert Rules
**************************************************************************/
/**
  * @desc   Calls Alert Rule List API method and fetches data
  * @param  accountCode string - Account Code
  * @return object - Alert Rule Data
*/
export const fetchAlertRulesList = (context, accountCode) => {
  return services.rules.retrieveRuleList(accountCode)
  .then((response) => {
    if (response.data.success) {
      return response.data.data
    } else {
      response.data.errors.forEach(function (error) {
        console.log(error)
      })
    }
  })
  .catch((error) => {
    console.error(error)
  })
}

/**
  * @desc   Calls Alert Rule Item Types API method and fetches data
  * @return object - Alert Rule Item Types Data
*/
export const fetchAlertRulesItemTypes = (context) => {
  return services.rules.retrieveRuleItemTypes()
  .then((response) => {
    if (response.data.success) {
      return response.data.data.items
    } else {
      response.data.errors.forEach(function (error) {
        console.log(error)
      })
    }
  })
  .catch((error) => {
    console.error(error)
  })
}

/**
  * @desc   Calls Alert Rule Operators API method and fetches data
  * @return object - Alert Rule Operators Data
*/
export const fetchAlertRulesOperators = (context) => {
  return services.rules.retrieveRuleOperators()
  .then((response) => {
    if (response.data.success) {
      return response.data.data.items
    } else {
      response.data.errors.forEach(function (error) {
        console.log(error)
      })
    }
  })
  .catch((error) => {
    console.error(error)
  })
}

/**
  * @desc   Calls Alert Rule Save API method
  * @param  parameters Object - Alert Rule Data
  * @return object - Response
*/
export const saveAlertRule = (context, parameters) => {
  return services.rules.saveAlertRule(parameters)
  .then((response) => {
    if (!response.data.success) {
      response.data.errors.forEach(function (error) {
        console.log(error)
      })
    }
    return response.data
  })
  .catch(() => {
    return {data: {success: false}}
  })
}

/**************************************************************************
Perishable Products
**************************************************************************/
/**
  * @desc   Calls Perishable Products Modes API method and fetches data
  * @return object - Perishable Products Data
*/
export const fetchPerishableProductsModes = (context) => {
  if (store.getters.productModes) {
    return store.getters.productModes
  } else {
    return services.products.retrievePerishableProductsModes().then((response) => {
      if (response.data.success) {
        context.commit(types.PRODUCT_SET_MODES, response.data.data.items)
        return response.data.data.items
      } else {
        response.data.errors.forEach(function (error) {
          console.log(error)
        })
      }
    })
    .catch((error) => {
      console.error(error)
    })
  }
}

/**
  * @desc   Calls Perishable Products List API method and fetches data
  * @param  parameters object - Perishable Products Filter
  * @return object - Perishable Products Data
*/
export const fetchPerishableProductsList = (context, parameters) => {
  return services.products.retrievePerishableProductsList(parameters)
  .then((response) => {
    if (response.data.success) {
      return response.data.data.products
    } else {
      response.data.errors.forEach(function (error) {
        console.log(error)
      })
    }
  })
  .catch((error) => {
    console.error(error)
  })
}

/**
  * @desc   Calls Document Status List API method
  * @return object - Document Status Data
*/
export const getDocumentStatus = (context) => {
  return services.orders.retrieveDocumentStatusData().then((response) => {
    if (response.data.success) {
      context.commit(types.ORDERPROGRESS_DOCUMENT_STATUS, response.data.data.items)
      return response.data.data.items
    } else {
      response.data.errors.forEach(function (error) {
        console.log(error)
      })
    }
  })
  .catch((error) => {
    console.error(error)
  })
}

/**
  * @desc   Calls Supplier/Customer List API method
  * @param  parameters object - Supplier/Customer Filter
  * @return object - Supplier/Customer Data
*/
export const getSupplierCustomer = (context, parameters) => {
  return services.orders.retrieveSupplierCustomerData(parameters).then((response) => {
    if (response.data.success) {
      context.commit(types.ORDERPROGRESS_DOCUMENT_SUPPLIERCUSTOMER, response.data.data.items)
      return response.data.data.items
    } else {
      response.data.errors.forEach(function (error) {
        console.log(error)
      })
    }
  })
  .catch((error) => {
    console.error(error)
  })
}

/**
  * @desc   Calls Orders Progress Data API method and fetches data
  * @param  parameters object - Client Array, Start Date and End Date
  * @return object - Orders Progress Data Object
*/
export const fetchOrdersProgressData = (context, parameters) => {
  return services.orders.retrieveProgressData(parameters)
  .then((response) => {
    if (response.data.success) {
      context.commit(types.ORDERPROGRESS_SET_ORDER_SEARCH, {
        result: response.data.data,
        params: parameters
      })
      return response.data.data
    } else {
      response.data.errors.forEach(function (error) {
        console.log(error)
      })
    }
  })
  .catch((error) => {
    console.error(error)
  })
}

/**************************************************************************
Inventory
**************************************************************************/

/**
  * @desc   Calls ERI Summary API method and fetches data
  * @param  parameters object - ERI Summary Filter
  * @return object - Eri Summary Data
*/
export const fetchEriSummaryData = (context, parameters) => {
  return services.inventory.retrieveEriSummaryData(parameters)
  .then((response) => {
    if (response.data.success) {
      return response.data.data.eris
    } else {
      response.data.errors.forEach(function (error) {
        console.log(error)
      })
    }
  })
  .catch((error) => {
    console.error(error)
  })
}

/**
  * @desc   Calls ERI Detail API method and fetches data
  * @param  parameters object - ERI Detail Filter
  * @return object - Eri Detail Data
*/
export const fetchEriDetailData = (context, parameters) => {
  return services.inventory.retrieveEriDetailData(parameters)
  .then((response) => {
    if (response.data.success) {
      return response.data.data
    } else {
      response.data.errors.forEach(function (error) {
        console.log(error)
      })
    }
  })
  .catch((error) => {
    console.error(error)
  })
}

/**
  * @desc   Calls ERI Detail API method and exports data
  * @param  parameters object - ERI Detail Filter
  * @return object - Eri Detail Data
*/
export const exportEriDetailData = (context, parameters) => {
  return services.inventory.exportEriDetailData(parameters)
  .then((response) => {
    if (response.data) {
      return response.data
    } else {
      response.data.errors.forEach(function (error) {
        console.log(error)
      })
    }
  })
  .catch((error) => {
    console.error(error)
  })
}

/**
  * @desc   Calls ERI Registry API method and evaluates response
  * @param  parameters object - ERI Data
  * @return object - Response
*/
export const inventoryEntry = (context, data) => {
  return services.inventory.eriEntry(data)
  .then((response) => {
    if (response.data.success) {
      return true
    } else {
      response.data.errors.forEach(function (error) {
        console.log(error)
      })
    }
  })
  .catch((error) => {
    console.error(error)
  })
}

export const getItemsForInventory = (context, accountCode) => {
  return services.inventory.retrieveItemsForInventoryData(accountCode).then((response) => {
    if (response.data.success) {
      context.commit(types.INVENTORY_SET_ITEMS, response.data.data.items)
      return response.data.data.items
    } else {
      response.data.errors.forEach(function (error) {
        console.log(error)
      })
    }
  })
  .catch((error) => {
    console.error(error)
  })
}

/**
  * @desc   Calls Inventory Product Status Data API method and fetches data
  * @param  accountCode string - Account Code
  * @return object - Inventory Product Status Data
*/
export const fetchInventoryProductStatusList = (context, parameters) => {
  return services.inventory.retrieveInventoryProductStatusData(parameters)
  .then((response) => {
    if (response.data.success) {
      return response.data.data
    } else {
      response.data.errors.forEach(function (error) {
        console.log(error)
      })
    }
  })
  .catch((error) => {
    console.error(error)
  })
}

/**
  * @desc   Calls Inventory Lines API method and fetches data
  * @param  accountCode object - Account Code Filter
  * @return object - Inventory Lines Data
*/
export const fetchInventoryLines = (context, accountCode) => {
  if (store.getters.inventoryLines[accountCode]) {
    return store.getters.inventoryLines[accountCode]
  } else {
    return services.inventory.retrieveInventoryLines(accountCode).then((response) => {
      if (response.data.success) {
        context.commit(types.INVENTORY_SET_LINES,
          {
            accountCode: accountCode,
            data: response.data.data.lines
          }
        )
        return response.data.data.lines
      } else {
        response.data.errors.forEach(function (error) {
          console.log(error)
        })
      }
    })
    .catch((error) => {
      console.error(error)
    })
  }
}

/**
  * @desc   Calls Inventory Status API method and fetches data
  * @param  accountCode object - Account Code Filter
  * @return object - Inventory Status Data
*/
export const fetchInventoryStatus = (context, accountCode) => {
  if (store.getters.inventoryStatuses[accountCode]) {
    return store.getters.inventoryStatuses[accountCode]
  } else {
    return services.inventory.retrieveInventoryStatus(accountCode)
    .then((response) => {
      if (response.data.success) {
        context.commit(types.INVENTORY_SET_STATUSES,
          {
            accountCode: accountCode,
            data: response.data.data.items
          }
        )
        return response.data.data.items
      } else {
        response.data.errors.forEach(function (error) {
          console.log(error)
        })
      }
    })
    .catch((error) => {
      console.error(error)
    })
  }
}

/**
  * @desc   Calls Document Types API method and fetches data
  * @return object - Document Types Data
*/
export const fetchDocumentTypes = (context) => {
  return services.documents.retrieveTypesData()
  .then((response) => {
    if (response.data.success) {
      return response.data.data.items
    } else {
      response.data.errors.forEach(function (error) {
        console.log(error)
      })
    }
  })
  .catch((error) => {
    console.error(error)
  })
}

export const getItemsForAccountConfiguration = (context, parameters) => {
  return services.documents.retrieveAccountConfigurationData(parameters).then((response) => {
    if (response.data.success) {
      return response.data.data.items
    } else {
      response.data.errors.forEach(function (error) {
        console.log(error)
      })
    }
  })
  .catch((error) => {
    console.error(error)
  })
}



// WEBPACK FOOTER //
// ./src/vuex/actions.js